import { MutableRefObject, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { Primitive } from 'type-fest';

import Analytics from '@hh.ru/analytics-js';
import { usePush } from '@hh.ru/redux-spa-middleware';
import { LangTrls } from 'bloko/common/hooks/useTranslations';

import { defaultMagritteRequestErrorHandler } from 'src/api/notifications/defaultRequestErrorHandler';
import {
    changeTopicError,
    changeTopicErrorBadAddrress,
    changeTopicErrorNoQa,
    type ChangeTopicErrorProps,
} from 'src/components/ChangeTopicForm/ChangeTopicError';
import { processOwnersTemplates, processGuestsTemplates } from 'src/components/ChangeTopicForm/utils/processTemplates';
import { useNotification } from 'src/components/Notifications/Provider';
import { EmployerStateTrlKeys } from 'src/components/VacancyFunnel/trls';
import NotificationComponent from 'src/components/VacancyResponses/Notifications';
import { useSelector } from 'src/hooks/useSelector';
import { changeTopicState, setLoadingAction } from 'src/models/candidatesList';
import { Template } from 'src/models/employerMailTemplates';
import { StateId } from 'src/models/employerNegotiations/stateId.types';
import { AssessmentTool, EmployerNegotiationsChangeTopicState } from 'src/models/employerNegotiationsChangeTopic';
import { ChangeTopicError } from 'src/models/employerNegotiationsChangeTopic/error.types';
import { EmployerStateType } from 'src/models/negotiationTopic.types';
import { fetcher } from 'src/utils/fetcher';

const CHANGE_TOPIC_URL = '/employer/negotiations/change_topic';
const CHANGE_TOPIC_CONTENT_URL = '/shards/employer/change_topic_content';
const FETCH_GET_MAIL_TEMPLATES = '/shards/employer/mail_templates/get_templates_by_employer_state';

interface ChangeTopicContentQueryParams {
    vacancyId?: number;
    state?: StateId;
    assesmentCode?: AssessmentTool;
    simhash?: string;
    toAnother?: boolean;
    resumeHash?: string[];
    t?: Primitive | Primitive[];
    funnelId?: number;
}

interface ChangeTopicBody {
    addressId?: number | null;
    funnelStageId?: number;
    mailText?: string | null;
    resumeHash?: string[] | string;
    simhash?: string;
    state?: string;
    toAnother?: boolean;
    vacancyId?: string | number;
    videoCallDurationInMinutes?: string;
    videoCallName?: string;
    videoCallStartTime?: string;
    funnelId?: string;
    t?: string[] | string;
    useAddressFromVacancy?: boolean;
}

interface FetchMailTemplatesQuery {
    employerManagerId?: number;
    employerState: EmployerStateType;
    anonymousTemplates?: boolean;
}

declare global {
    interface FetcherGetApi {
        [CHANGE_TOPIC_CONTENT_URL]: {
            queryParams: ChangeTopicContentQueryParams;
            response: { employerNegotiationsChangeTopic: EmployerNegotiationsChangeTopicState };
        };
        [FETCH_GET_MAIL_TEMPLATES]: {
            queryParams: FetchMailTemplatesQuery;
            response: { ownersTemplates: Template[]; currentEmployerTemplates: Template[] };
        };
    }

    interface FetcherPostApi {
        [CHANGE_TOPIC_URL]: {
            queryParams: void;
            body: ChangeTopicBody;
            response: {
                state: StateId;
                assessmentCode: AssessmentTool;
                vacancyId: number;
                redirectUrl: string;
            };
        };
    }
}

interface FetcherErrorData {
    errors: { key: ChangeTopicError | string }[];
}

interface Values {
    funnelStageId?: number;
    funnelId?: string;
    resumeHash: string;
    state: StateId;
    t: string;
    vacancyId: number;
    trls: LangTrls;
}

type UseOneClickChangeTopicStateHandlerHook = (abortController: MutableRefObject<AbortController | null>) => {
    handleSubmit: (values: Values) => Promise<void>;
};

const getAnalyticsLabel = (state: StateId, assessmentCode: AssessmentTool, withLetter: boolean): string => {
    if (state === StateId.Assessment) {
        return assessmentCode;
    }
    if (withLetter) {
        return 'send-message';
    }
    return '';
};

const CHANGE_TOPIC_BASE_URL = '/employer/negotiations/change_topic';

const useOneClickChangeTopicStateHandler: UseOneClickChangeTopicStateHandlerHook = (abortController) => {
    const dispatch = useDispatch();
    const push = usePush();
    const { addNotification } = useNotification();
    const changeTopicValues = useRef<ChangeTopicBody>();

    const employerManagerId = useSelector((state) => state.vacancyFunnel?.ownerEmployerManagerId);

    const handleSubmit = useCallback(
        async (values: Values): Promise<undefined> => {
            const { funnelStageId, trls, funnelId, ...params } = values;
            dispatch(setLoadingAction({ isLoading: true, funnelStageId }));

            // Фетчим change topic content, чтобы подготовить данные для перевода в новый статус
            let changeTopicContent;
            try {
                abortController.current?.abort();
                abortController.current = new AbortController();
                changeTopicContent = await fetcher.get(CHANGE_TOPIC_CONTENT_URL, {
                    params: { ...params, resumeHash: [params.resumeHash] },
                    signal: abortController.current.signal,
                });
            } catch (error) {
                defaultMagritteRequestErrorHandler(error, addNotification);
                return;
            } finally {
                dispatch(setLoadingAction({ isLoading: false }));
            }

            const { substates, ...rootState } =
                changeTopicContent.employerNegotiationsChangeTopic.changeTopic.availableStagesForSelectedState;
            const isSubstate = !(rootState.funnelStageId === funnelStageId);
            const selectedStage = !isSubstate
                ? { ...rootState }
                : substates.find((substate) => substate.funnelStageId === funnelStageId);
            const templateInfo = selectedStage?.templateInfo;
            const employerState = selectedStage?.state;

            let templatesResult;

            if (templateInfo) {
                const useAnonymousTemplate =
                    changeTopicContent.employerNegotiationsChangeTopic.changeTopic.useAnonymousTemplate;
                try {
                    abortController.current?.abort();
                    abortController.current = new AbortController();
                    templatesResult = await fetcher.get(FETCH_GET_MAIL_TEMPLATES, {
                        params: {
                            employerState: employerState?.toUpperCase() as EmployerStateType,
                            employerManagerId,
                            ...(!useAnonymousTemplate && { anonymousTemplates: false }),
                        },
                        signal: abortController.current.signal,
                    });
                } catch (error) {
                    defaultMagritteRequestErrorHandler(error, addNotification);
                }
                // Переводить топики по воркфлоу может не только владелец вакансии, но и другой менеджер с определенными правми
                // Поэтому если гость переводит топики, то должны использовать его шаблоны, а не владельца
                if (!templatesResult?.currentEmployerTemplates.length && templatesResult?.ownersTemplates.length) {
                    const { mailTemplates, smsTemplates } = processOwnersTemplates(
                        templatesResult.ownersTemplates,
                        templateInfo.employerManagerTemplateId,
                        templateInfo.smsEmployerManagerTemplateId,
                        templateInfo.usedTemplateType,
                        templateInfo.usedSmsTemplateType,
                        useAnonymousTemplate
                    );
                    const selectedMailTemplate = mailTemplates.find((template) => template.isSelected);
                    const selectedSmsTemplate = smsTemplates.find((template) => template.isSelected);

                    templateInfo.template = selectedMailTemplate?.template || '';
                    templateInfo.smsTemplate = selectedSmsTemplate?.template || '';
                } else if (templatesResult?.currentEmployerTemplates.length) {
                    const { mailTemplates, smsTemplates } = processGuestsTemplates(
                        templatesResult.currentEmployerTemplates,
                        templateInfo.usedTemplateType,
                        templateInfo.usedSmsTemplateType
                    );

                    const selectedMailTemplate = mailTemplates.find((template) => template.primaryTemplate);
                    const selectedSmsTemplate = smsTemplates.find((template) => template.primaryTemplate);

                    templateInfo.template = selectedMailTemplate?.template || '';
                    templateInfo.smsTemplate = selectedSmsTemplate?.template || '';
                }
            }

            const isWarningAndNoMailText =
                (templateInfo?.sendMessageRequired || templateInfo?.sendMessageRequiredWarning) &&
                !templateInfo.sendMessagePredefinedByUser;

            if (isWarningAndNoMailText) {
                let url = `${CHANGE_TOPIC_BASE_URL}?vacancyId=${values.vacancyId}&t=${values.t}&state=${values.state}`;
                if (funnelStageId) {
                    url += `&collectionId=${values.state}_${funnelStageId}`;
                }

                push(url);
                return;
            }

            const isMailText =
                templateInfo?.sendMessageRequired ||
                templateInfo?.sendMessageRequiredWarning ||
                templateInfo?.sendMessagePredefinedByUser;
            const isSmsAllowed = isMailText && templateInfo.smsAllowed;
            const isSmsText = templateInfo?.sendSmsPredefinedByUser && templateInfo?.smsTemplate?.trim();
            const isAddressAllowed = templateInfo?.addressAllowed;
            const useAddressFromVacancy = templateInfo?.useAddressFromVacancy;
            const addressId = useAddressFromVacancy ? null : templateInfo?.addressId;

            changeTopicValues.current = {
                state: employerState,
                vacancyId: values.vacancyId,
                resumeHash: values.resumeHash,
                funnelStageId: selectedStage?.funnelStageId,
                ...(funnelId && { funnelId }),
                ...(isMailText && templateInfo.template?.trim() && { mailText: templateInfo.template }),
                ...(isSmsAllowed && isSmsText && { smsText: templateInfo.smsTemplate }),
                ...(isMailText && isAddressAllowed && addressId && { addressId }),
                useAddressFromVacancy,
            };
            // Переводим топик в новый статус
            let changeTopicResult;
            try {
                changeTopicResult = await fetcher.postFormData(CHANGE_TOPIC_URL, changeTopicValues.current);
            } catch (error) {
                if (axios.isAxiosError(error) && error.response) {
                    const data = error.response.data as FetcherErrorData;
                    if (error.response.status < 500 && data?.errors) {
                        const errors = data.errors;
                        errors.forEach(({ key }) => {
                            if (key === ChangeTopicError.NoFaService) {
                                addNotification(changeTopicErrorNoQa);
                            } else if (key === ChangeTopicError.AddressUnavailable) {
                                addNotification(changeTopicErrorBadAddrress, {
                                    props: {
                                        vacancyId: values.vacancyId,
                                    },
                                });
                            } else {
                                addNotification(changeTopicError, {
                                    props: { errorCode: key } as ChangeTopicErrorProps,
                                });
                            }
                        });
                    } else {
                        addNotification(changeTopicError, {
                            props: { errorCode: 'networkError' },
                        });
                    }
                } else {
                    defaultMagritteRequestErrorHandler(error, addNotification);
                }
            } finally {
                dispatch(setLoadingAction({ isLoading: false }));
            }

            if (changeTopicResult?.data) {
                const { state, assessmentCode } = changeTopicResult.data;

                if (state === StateId.Interview || state === StateId.PhoneInterview) {
                    Analytics.sendEvent('employer', 'invitation', changeTopicResult.data.state);
                } else {
                    Analytics.sendEvent(
                        'employer',
                        `vacancy-status-${state}`,
                        getAnalyticsLabel(state, assessmentCode, !!changeTopicValues?.current?.mailText)
                    );
                }
                dispatch(
                    changeTopicState({
                        topicId: values.t,
                        collectionToId: selectedStage?.collectionId,
                        isSubstatesExp: true,
                    })
                );
                addNotification(NotificationComponent, {
                    props: {
                        actionType: isSubstate ? 'topicStateChangedToSubstate' : 'topicStateChangedToState',
                        stage: isSubstate
                            ? selectedStage?.name
                            : trls[EmployerStateTrlKeys[values.state.toUpperCase() as EmployerStateType]],
                        type: 'success',
                    },
                });
            }
        },
        [addNotification, dispatch, push, abortController, employerManagerId]
    );

    return { handleSubmit };
};

export { useOneClickChangeTopicStateHandler };
